'use client';
// MarketCard Component - Modern Design (Fully Responsive, No anonymous functions)
import { useMemo } from 'react';
import Link from 'next/link';
import { Box, Card, CardContent, Theme } from '@mui/material';

import {
  ChanceIndicator,
  MarketCardFooter,
  MarketCardHeader,
  MarketCardVariants,
} from '@/components';
import { useEventBookmarkAction } from '@/hooks';
import type { EventMarket, EventOutcome, EventResponse } from '@/types';
import {
  filterAndSortMarkets,
  findMarket,
  formatPercentage,
  getEventType,
  getMarketProbability,
  isEventLive,
  isMarketResolved,
  parseMarketOutcomes,
} from '@/utils';

import { ROUTES, UMA_RESOLUTION_STATUS } from '@/constants';

interface MarketCardProps {
  event: EventResponse;
  market: EventMarket;
  onTradeClick?: (marketId: string, outcomeId: string, isBuy: boolean) => void;
  activeCategorySlug?: string;
  selectedFilter?: string;
  alwaysShowBookmark?: boolean;
}

export function MarketCard({
  event,
  market,
  onTradeClick,
  activeCategorySlug,
  selectedFilter,
  alwaysShowBookmark = false,
}: MarketCardProps) {
  const outcomes = useMemo(() => {
    if (!market?.outcomes) return [];
    return parseMarketOutcomes(market.outcomes);
  }, [market?.outcomes]);

  const { checkIsBookmarked, handleBookmarkToggle } = useEventBookmarkAction();
  const isBookmarked = checkIsBookmarked(event.id);

  const isResolved = Boolean(
    isMarketResolved(market) || market.umaResolutionStatus === UMA_RESOLUTION_STATUS.RESOLVED
  );

  const markets = event.markets || [];
  const sortedMarkets = filterAndSortMarkets(markets);
  const eventType = getEventType(event);
  const chance = getMarketProbability(market);
  const isGroupedEvent =
    (eventType === 'MULTI_OUTCOME' || eventType === 'MULTI_QUESTION') && sortedMarkets.length > 1;

  const rawTitle = isResolved ? event.markets[0].question : (event?.title ?? '-');

  const isCryptoMinuteFilter =
    activeCategorySlug === 'crypto' &&
    selectedFilter != null &&
    (selectedFilter?.toLowerCase().includes('5 min') ||
      selectedFilter?.toLowerCase().includes('15 min'));

  const marketTitle = isCryptoMinuteFilter
    ? `${rawTitle.split('-')[0].trim()} - ${selectedFilter}`
    : rawTitle;
  const totalVolume = event?.volume ?? 0;
  const image = event?.image ?? '';
  const footerEndDate = market.umaEndDate || market.closedTime || market.endDate;
  const isGame = !!event.gameId;
  const gameUrl =
    isGame && event?.gameId
      ? ROUTES.GAME_DETAIL(event?.homeTeam?.league || 'live', event?.id.toString())
      : undefined;

  const getOutcomes = (market?: EventMarket): EventOutcome[] => {
    if (!market) return [];
    try {
      return typeof market.outcomes === 'string'
        ? JSON.parse(market.outcomes)
        : (market.outcomes as EventOutcome[]) || [];
    } catch (e) {
      return [];
    }
  };

  const moneylineMarket = findMarket(event, 'moneyline');
  const mlOutcomes = getOutcomes(moneylineMarket);
  const isLive = isEventLive(event, activeCategorySlug);

  const team1 = {
    abbr: event.homeTeam?.abbreviation?.toUpperCase() || 'HME',
    name: event.homeTeam?.name || 'Team 1',
    color: event.homeTeam?.color || '#2563eb',
    logo: event.homeTeam?.logo,
    percentage: mlOutcomes[0]?.price != null ? formatPercentage(mlOutcomes[0].price, 0) : undefined,
  };

  const team2 = {
    abbr: event.awayTeam?.abbreviation?.toUpperCase() || 'AWY',
    name: event.awayTeam?.name || 'Team 2',
    color: event.awayTeam?.color || '#dc2626',
    logo: event.awayTeam?.logo,
    percentage: mlOutcomes[1]?.price != null ? formatPercentage(mlOutcomes[1].price, 0) : undefined,
  };

  const handleBookmarkClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    handleBookmarkToggle(e, event, 'market');
  };

  return (
    <Link
      href={
        isGame
          ? gameUrl || '#'
          : isLive
            ? ROUTES.LIVE_MARKET_DETAILS(event?.slug || market?.id)
            : ROUTES.MARKET_DETAILS(event?.slug || market?.id)
      }
      style={{
        textDecoration: 'none',
        display: 'block',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      <Card
        sx={{
          height: 260,
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: (theme: Theme) =>
              theme.palette.mode === 'dark'
                ? '0 4px 12px rgba(0,0,0,0.4)'
                : '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            p: 1.5,
            overflow: 'hidden',
            '&:last-child': { pb: 1.5 },
          }}
        >
          <MarketCardHeader
            title={marketTitle}
            imageUrl={image}
            sx={{ mb: 1.5 }}
            action={!isResolved && !isGroupedEvent && <ChanceIndicator percentage={chance} />}
            isGame={isGame}
            team1={team1}
            team2={team2}
            gameUrl={gameUrl}
            isResolved={isResolved}
          />
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: isResolved ? 'center' : 'flex-start',
              overflowY: 'auto',
              mt: 1.5,
              mb: isResolved ? 0 : 1.5,
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: '10px' },
            }}
          >
            <MarketCardVariants
              eventType={eventType}
              event={event}
              market={market}
              outcomes={outcomes}
              onTradeClick={onTradeClick}
              sortedMarkets={sortedMarkets}
              isFullWidthActions={false}
            />
          </Box>
          <MarketCardFooter
            volume={totalVolume}
            isBookmarked={isBookmarked}
            onBookmarkClick={handleBookmarkClick}
            isResolved={isResolved}
            endDate={footerEndDate}
            isLive={isLive}
            alwaysShowBookmark={alwaysShowBookmark}
          />
        </CardContent>
      </Card>
    </Link>
  );
}

export default MarketCard;
